// Projeler sayfası - Proje listesi ve yönetimi
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  FiPlus, 
  FiSearch, 
  FiEdit, 
  FiTrash2, 
  FiDownload,
  FiFolder,
  FiLock,
  FiX
} from 'react-icons/fi';
import { IoFilterCircleOutline } from 'react-icons/io5';
import { CgMenuGridO } from 'react-icons/cg';
import { projectService } from '../../services/projectService';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ProjectModal from '../../components/Projects/ProjectModal';
import DeleteConfirmModal from '../../components/Common/DeleteConfirmModal';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

const Projects = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [filters, setFilters] = useState({ name: '', customer: '', status: '', assigned_to: [], opportunity: [], close_date: '' });
  const [tempFilters, setTempFilters] = useState({ name: '', customer: '', assigned_to: [], opportunity: [], close_date: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);

  // Fırsat değerini rakam + metin karşılığına çeviren fonksiyon
  const getOpportunityText = (value) => {
    switch (value) {
      case '0': return '0 (Henüz belli değil)';
      case '1': return '1 (Çok Kötü)';
      case '2': return '2 (Kötü)';
      case '3': return '3 (Orta)';
      case '4': return '4 (İyi)';
      case '5': return '5 (Çok İyi)';
      default: return value;
    }
  };

  // Proje atanmış kullanıcıları al (mevcut filtrelere göre)
  const { data: assigneesData } = useQuery(
    ['projectAssignees', searchTerm, filters.status, filters.customer, filters.name, filters.opportunity],
    () => projectService.getProjectAssignees({
      search: searchTerm,
      status: filters.status,
      customer: filters.customer,
      name: filters.name,
      opportunity: (filters.opportunity && filters.opportunity.length > 0) ? filters.opportunity.join(',') : ''
    }),
    { keepPreviousData: true }
  );

  // Proje listesini al
  const { data: projectsData, isLoading } = useQuery(
    ['projects', currentPage, searchTerm, filters, itemsPerPage],
    () => projectService.getProjects({
      page: currentPage,
      search: searchTerm,
      status: filters.status,
      customer: filters.customer,
      name: filters.name,
      assigned_to: (filters.assigned_to && filters.assigned_to.length > 0) ? filters.assigned_to.join(',') : '',
      opportunity: (filters.opportunity && filters.opportunity.length > 0) ? filters.opportunity.join(',') : '',
      limit: itemsPerPage
    }),
    { keepPreviousData: true }
  );

  // Proje silme mutation'ı
  const deleteProjectMutation = useMutation(
    projectService.deleteProject,
    {
      onSuccess: () => {
        queryClient.invalidateQueries('projects');
        toast.success('Proje başarıyla silindi');
        setShowDeleteModal(false);
        setSelectedProject(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Proje silinemedi');
      }
    }
  );

  // Modal açma fonksiyonları
  const handleAddProject = () => {
    setEditingProject(null);
    setShowModal(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setShowModal(true);
  };

  const handleDeleteProject = (project) => {
    setSelectedProject(project);
    setShowDeleteModal(true);
  };

  // Silme onayı
  const confirmDelete = () => {
    if (selectedProject) {
      deleteProjectMutation.mutate(selectedProject.id);
    }
  };

  // Geçici arama terimi değişikliği (sadece input'u günceller)
  const handleTempSearchChange = (e) => {
    setTempSearchTerm(e.target.value);
  };

  // Enter tuşuna basınca aramayı uygula
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setSearchTerm(tempSearchTerm);
      setCurrentPage(1);
    }
  };

  const handleFilterChange = (type, value) => {
    setFilters(prev => ({ ...prev, [type]: value }));
    setCurrentPage(1);
  };

  // Geçici filtre değişikliği (sadece input'u günceller)
  const handleTempFilterChange = (type, value) => {
    setTempFilters(prev => ({ ...prev, [type]: value }));
  };

  // Enter tuşuna basınca filtreyi uygula
  const handleFilterKeyDown = (type, e) => {
    if (e.key === 'Enter') {
      setFilters(prev => ({ ...prev, [type]: tempFilters[type] }));
      setCurrentPage(1);
      // Enter'a basınca filtre inputunu kapat
      setShowFilters(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };
  const [showFilters, setShowFilters] = useState({ name: false, customer: false, status: false, assigned_to: false, opportunity: false, close_date: false });
  const toggleFilter = (key) => setShowFilters(prev => ({ ...prev, [key]: !prev[key] }));
  const [showActionMenu, setShowActionMenu] = useState(null);
  const toggleActionMenu = (id, event) => {
    if (showActionMenu === id) setShowActionMenu(null);
    else {
      setShowActionMenu(id);
      setTimeout(() => {
        const button = event.target.closest('button');
        if (button) {
          const rect = button.getBoundingClientRect();
          const menu = document.querySelector('.action-menu-dropdown');
          if (menu) {
            menu.style.left = `${rect.right - 192}px`;
            menu.style.top = `${rect.bottom + 4}px`;
          }
        }
      }, 0);
    }
  };

  // URL parametrelerini okuyup filtreleme yap
  useEffect(() => {
    const status = searchParams.get('status');
    const assigned_to = searchParams.get('assigned_to');
    
    if (status) {
      setFilters(prev => ({ ...prev, status: status }));
    }
    
    if (assigned_to) {
      setFilters(prev => ({ ...prev, assigned_to: assigned_to }));
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionMenu && !event.target.closest('.action-menu')) setShowActionMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionMenu]);

  // Filtreler değiştiğinde tempFilters'ı senkronize et
  useEffect(() => {
    setTempFilters({
      name: filters.name,
      customer: filters.customer,
      assigned_to: filters.assigned_to
    });
  }, [filters.name, filters.customer]);

  // searchTerm değiştiğinde tempSearchTerm'i senkronize et
  useEffect(() => {
    setTempSearchTerm(searchTerm);
  }, [searchTerm]);

  // Tablo dışına tıklayınca filtreleri kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Eğer tıklanan element tablo veya filtre içinde değilse filtreleri kapat
      const tableElement = document.querySelector('.projects-table');
      if (tableElement && !tableElement.contains(event.target)) {
        setShowFilters({ name: false, customer: false, status: false, assigned_to: false, opportunity: false, close_date: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Excel export fonksiyonu
  const handleExportExcel = () => {
    
    // Veri yapısını kontrol et
    let dataToExport = projectsData;
    if (projectsData && typeof projectsData === 'object' && !Array.isArray(projectsData)) {
      // Eğer object ise, data property'sini kontrol et
      if (projectsData.data && Array.isArray(projectsData.data)) {
        dataToExport = projectsData.data;
      } else if (projectsData.projects && Array.isArray(projectsData.projects)) {
        dataToExport = projectsData.projects;
      }
    }
    
    
    if (!dataToExport || !Array.isArray(dataToExport) || dataToExport.length === 0) {
      toast.error('Export edilecek proje bulunamadı');
      return;
    }

    const exportData = dataToExport.map(project => ({
      'Proje Adı': project.project_name,
      'Müşteri': project.customer_name || '-',
      'Atanan Kişi': project.assigned_to_name || '-',
      'Durum': project.project_status,
      'Oluşturan': project.created_by_name || '-',
      'Oluşturulma Tarihi': new Date(project.created_at).toLocaleDateString('tr-TR'),
      'Kapanma Tarihi': project.close_date ? new Date(project.close_date).toLocaleDateString('tr-TR') : '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Projeler');

    const fileName = `projeler_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    toast.success('Excel dosyası başarıyla indirildi');
  };

  // Aktif filtre kontrolü
  const hasActiveFilters = () => {
    return searchTerm || filters.name || filters.customer || filters.status || (filters.assigned_to && filters.assigned_to.length > 0) || (filters.opportunity && filters.opportunity.length > 0) || filters.close_date;
  };

  // Tüm filtreleri temizle
  const clearAllFilters = () => {
    setSearchTerm('');
    setTempSearchTerm('');
    setFilters({ name: '', customer: '', status: '', assigned_to: [], opportunity: [], close_date: '' });
    setTempFilters({ name: '', customer: '', assigned_to: [], opportunity: [], close_date: '' });
    setShowFilters({ name: false, customer: false, status: false, assigned_to: false, opportunity: false, close_date: false });
    setCurrentPage(1);
  };

  // Sayfa değiştirme
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Para birimi formatı
  const formatCurrency = (amount, currency) => {
    const cur = currency || 'TRY';
    const currencyCode = cur === 'TL' ? 'TRY' : cur;
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(amount || 0));
  };

  const projects = projectsData?.projects || [];
  const filteredAndSorted = useMemo(() => {
    let list = [...projects];
    if (filters.name) list = list.filter(p => (p.project_name || '').toLowerCase().includes(filters.name.toLowerCase()));
    if (filters.customer) list = list.filter(p => (p.customer_name || '').toLowerCase().includes(filters.customer.toLowerCase()));
    if (filters.status) list = list.filter(p => String(p.project_status) === String(filters.status));
    if (filters.assigned_to && filters.assigned_to.length > 0) list = list.filter(p => filters.assigned_to.includes(String(p.assigned_to)));
    if (filters.opportunity && filters.opportunity.length > 0) list = list.filter(p => filters.opportunity.includes(String(p.project_opportunity)));
    if (filters.close_date) {
      const filterDate = new Date(filters.close_date).toISOString().split('T')[0];
      list = list.filter(p => {
        if (!p.close_date) return false;
        const projectDate = new Date(p.close_date).toISOString().split('T')[0];
        return projectDate === filterDate;
      });
    }
    if (sortConfig.key) {
      list.sort((a, b) => {
        if (sortConfig.key === 'latest_offer_ratio') {
          const aTotal = Number(a.latest_offer_total || 0);
          const bTotal = Number(b.latest_offer_total || 0);
          const aProfit = Number(a.latest_profitability || 0);
          const bProfit = Number(b.latest_profitability || 0);
          const av = aTotal > 0 ? (aProfit / aTotal) : -Infinity;
          const bv = bTotal > 0 ? (bProfit / bTotal) : -Infinity;
          if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
          if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
        const numericKeys = new Set(['latest_offer_total', 'latest_profitability']);
        const dateKeys = new Set(['created_at', 'close_date']);
        const avRaw = a[sortConfig.key];
        const bvRaw = b[sortConfig.key];
        let av, bv;
        if (dateKeys.has(sortConfig.key)) {
          av = avRaw ? new Date(avRaw).getTime() : 0;
          bv = bvRaw ? new Date(bvRaw).getTime() : 0;
        } else if (numericKeys.has(sortConfig.key)) {
          av = Number(avRaw || 0);
          bv = Number(bvRaw || 0);
        } else {
          av = (avRaw ?? '').toString().toLowerCase();
          bv = (bvRaw ?? '').toString().toLowerCase();
        }
        if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [projects, filters, sortConfig]);

  // Mevcut tablodaki projelerin toplam tutarları - para birimine göre ayrı
  const tableStats = useMemo(() => {
    const init = {
      TRY: { amount: 0, profit: 0, count: 0 },
      USD: { amount: 0, profit: 0, count: 0 },
      totalCount: 0,
    };
    if (!filteredAndSorted || filteredAndSorted.length === 0) return init;

    return filteredAndSorted.reduce((agg, project) => {
      const curRaw = (project.latest_offer_currency || 'TRY');
      const key = curRaw === 'USD' ? 'USD' : (curRaw === 'TL' ? 'TRY' : 'TRY');
      const amount = Number(project.latest_offer_total || 0);
      const profit = Number(project.latest_profitability || 0);
      
      // Sadece tutarı olan projeleri say (tutar > 0)
      if (amount > 0) {
        agg[key].amount += amount;
        agg[key].profit += profit;
        agg[key].count += 1;
        agg.totalCount += 1;
      }
      
      return agg;
    }, init);
  }, [filteredAndSorted]);

  const pagination = projectsData?.pagination || {};

  if (isLoading && !projectsData) {
    return <LoadingSpinner text="Projeler yükleniyor..." fullScreen={false} />;
  }

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı ve aksiyonlar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-gray-600 dark:text-gray-400">
            <FiFolder size={24} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Projeler
          </h1>
        </div>
        
        {/* Guest kullanıcılar için yeni proje butonunu gizle */}
        {currentUser?.user_type !== 'guest' && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={handleAddProject}
              className="btn-primary flex items-center"
            >
              <FiPlus className="mr-2" size={16} />
              Yeni Proje
            </button>
          </div>
        )}
      </div>

      {/* Arama ve Export */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Arama */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Proje ara... (Enter ile ara)"
              value={tempSearchTerm}
              onChange={handleTempSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="input-field pl-10"
            />
          </div>

          <button 
            onClick={handleExportExcel}
            className="btn-secondary flex items-center justify-center px-4 py-2"
          >
            <FiDownload className="mr-2" size={16} />
            Excel
          </button>
        </div>
      </div>

      {/* Filtre Uyarı Mesajı */}
      {hasActiveFilters() && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Filtre uygulanmıştır
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">
                  {searchTerm && `Arama: "${searchTerm}"`}
                  {filters.name && ` • Proje Adı: "${filters.name}"`}
                  {filters.customer && ` • Müşteri: "${filters.customer}"`}
                  {filters.status && ` • Durum: "${filters.status}"`}
                  {filters.assigned_to && filters.assigned_to.length > 0 && ` • Account Manager: ${filters.assigned_to.length} seçildi`}
                  {filters.opportunity && filters.opportunity.length > 0 && ` • Fırsat: ${filters.opportunity.length} seçildi`}
                  {filters.close_date && ` • Kapanma: ${new Date(filters.close_date).toLocaleDateString('tr-TR')}`}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-200 dark:bg-blue-800 dark:hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FiX className="mr-1" size={14} />
                Tüm Filtreleri Temizle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proje listesi - Mobil 4 sütun grid (sm altında), her satırda tek proje */}
      <div className="card p-4 sm:p-6 sm:hidden">
        {/* Açıklama: Mobilde tablo yerine 4 sütunlu satır bazlı grid gösterilir */}
        <div className="grid grid-cols-4 gap-2 text-[10px] font-semibold text-gray-500 dark:text-gray-400 px-1">
          <div>Müşteri</div>
          <div>Proje</div>
          <div>Durum</div>
          <div>Tarih</div>
        </div>
        <div className="mt-2">
          {filteredAndSorted.length > 0 ? (
            filteredAndSorted.map((project) => (
              <div
                key={project.id}
                onClick={()=> window.location.href = `/projects/${project.project_uuid}`}
                className="grid grid-cols-4 gap-2 items-center px-2 py-3 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {/* Müşteri adı - Proje adından önce */}
                <div className="text-[11px] text-gray-700 dark:text-gray-300 truncate">
                  {project.customer_name || '-'}
                </div>
                {/* Proje adı - Müşteri adından sonra */}
                <div className="pr-2">
                  <div className="text-[11px] text-gray-900 dark:text-white font-medium truncate">
                    {project.project_name}
                  </div>
                  <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                    {project.assigned_to_name || '-'}
                  </div>
                </div>
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    project.project_status === 'Aktif' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    project.project_status === 'Tamamlandı' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    project.project_status === 'İptal' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}>
                    {project.project_status}
                  </span>
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 text-right">
                  {new Date(project.created_at).toLocaleDateString('tr-TR')}
                </div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Henüz proje bulunmuyor</div>
          )}
        </div>

        {/* Mobil sayfalama: Önceki/Sonraki ve mevcut sayfa bilgisi */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <button
                onClick={() => handlePageChange(Math.max(1, Number(currentPage) - 1))}
                disabled={Number(currentPage) <= 1}
                className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                  Number(currentPage) <= 1
                    ? 'text-gray-400 border-gray-200 dark:text-gray-600 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-brand-primary hover:text-white'
                }`}
              >
                Önceki
              </button>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Sayfa {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(Number(pagination.totalPages) || 1, Number(currentPage) + 1))}
                disabled={Number(currentPage) >= Number(pagination.totalPages)}
                className={`px-3 py-2 text-xs rounded-lg transition-colors border ${
                  Number(currentPage) >= Number(pagination.totalPages)
                    ? 'text-gray-400 border-gray-200 dark:text-gray-600 dark:border-gray-700 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-brand-primary hover:text-white'
                }`}
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Proje tablosu - Masaüstü (sm ve üzeri) */}
      <div className="card hidden sm:block">
        <div className="table-container overflow-x-auto">
          <table className="table w-full table-fixed text-xs projects-table">
            <thead>
              <tr>
                {/* MÜŞTERİ ADI sütunu - Proje adından önce */}
                <th className="relative w-[12%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('customer_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      MÜŞTERİ ADI
                      {sortConfig.key === 'customer_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('customer')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.customer && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="text" 
                        placeholder="Filtrele... (Enter ile uygula)" 
                        value={tempFilters.customer} 
                        onChange={(e) => handleTempFilterChange('customer', e.target.value)} 
                        onKeyDown={(e) => handleFilterKeyDown('customer', e)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                {/* PROJE ADI sütunu - Müşteri adından sonra */}
                <th className="relative w-[15%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('project_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      PROJE ADI
                      {sortConfig.key === 'project_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('name')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.name && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="text" 
                        placeholder="Filtrele... (Enter ile uygula)" 
                        value={tempFilters.name} 
                        onChange={(e) => handleTempFilterChange('name', e.target.value)} 
                        onKeyDown={(e) => handleFilterKeyDown('name', e)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="relative w-[10%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('assigned_to_name')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      ACCOUNT MANAGER
                      {sortConfig.key === 'assigned_to_name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('assigned_to')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.assigned_to && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2 min-w-[200px]">
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {assigneesData?.assignees && assigneesData.assignees.length > 0 ? (
                          assigneesData.assignees.map((user) => (
                            <label key={user.id} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={filters.assigned_to.includes(String(user.id))}
                                onChange={() => handleFilterChange('assigned_to', filters.assigned_to.includes(String(user.id)) ? filters.assigned_to.filter(v=>v!==String(user.id)) : [...filters.assigned_to, String(user.id)])}
                                className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                              />
                              <span className="text-xs text-gray-700 dark:text-gray-300">{user.user_fullname}</span>
                            </label>
                          ))
                        ) : (
                          <div className="text-xs text-gray-500 dark:text-gray-400 p-2">Listelenecek kullanıcı yok</div>
                        )}
                      </div>
                      {filters.assigned_to.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => handleFilterChange('assigned_to', [])}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Tümünü Temizle
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </th>
                <th className="w-[8%]">
                  <button onClick={()=>handleSort('latest_offer_total')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                    PROJE TUTARI
                    {sortConfig.key === 'latest_offer_total' && (
                      <span className="ml-1">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="w-[8%]">
                  <button onClick={()=>handleSort('latest_profitability')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                    KARLILIK
                    {sortConfig.key === 'latest_profitability' && (
                      <span className="ml-1">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="w-[8%]">
                  <button onClick={()=>handleSort('latest_offer_ratio')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                  KARLILIK (%)
                    {sortConfig.key === 'latest_offer_ratio' && (
                      <span className="ml-1">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="relative w-[8%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('project_status')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      PROJE DURUMU
                      {sortConfig.key === 'project_status' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('status')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.status && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <select value={filters.status} onChange={(e)=>handleFilterChange('status', e.target.value)} className="w-full px-2 py-1 text-xs border border-gray-300 rounded">
                        <option value="">Tümü</option>
                        <option value="Aktif">Aktif</option>
                        <option value="Beklemede">Beklemede</option>
                        <option value="Başarılı Tamamlandı">Başarılı Tamamlandı</option>
                        <option value="Başarısız Tamamlandı">Başarısız Tamamlandı</option>
                        <option value="İptal">İptal</option>
                      </select>
                    </div>
                  )}
                </th>
                <th className="relative w-[6%]">
                  <div className="flex items-center justify-between">
                    <span>Fırsat</span>
                    <button onClick={()=>toggleFilter('opportunity')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.opportunity && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2 min-w-[200px]">
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {['0','1','2','3','4','5'].map((val) => (
                          <label key={val} className="flex items-center space-x-2 p-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filters.opportunity.includes(val)}
                              onChange={() => handleFilterChange('opportunity', filters.opportunity.includes(val) ? filters.opportunity.filter(v=>v!==val) : [...filters.opportunity, val])}
                              className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <span className="text-xs text-gray-700 dark:text-gray-300">{getOpportunityText(val)}</span>
                          </label>
                        ))}
                      </div>
                      {filters.opportunity.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <button
                            onClick={() => handleFilterChange('opportunity', [])}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Tümünü Temizle
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </th>
                <th className="relative w-[8%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('created_at')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      OLUŞTURULMA
                      {sortConfig.key === 'created_at' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                  </div>
                </th>
                <th className="relative w-[8%]">
                  <div className="flex items-center justify-between">
                    <button onClick={()=>handleSort('close_date')} className="text-left hover:text-blue-600 cursor-pointer flex items-center">
                      KAPANMA
                      {sortConfig.key === 'close_date' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </button>
                    <button onClick={()=>toggleFilter('close_date')} className="text-gray-400 hover:text-gray-600"><IoFilterCircleOutline size={16}/></button>
                  </div>
                  {showFilters.close_date && (
                    <div className="absolute top-full left-0 right-0 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-2">
                      <input 
                        type="date" 
                        placeholder="Tarih seçin" 
                        value={tempFilters.close_date} 
                        onChange={(e) => handleTempFilterChange('close_date', e.target.value)} 
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleFilterChange('close_date', tempFilters.close_date);
                            setShowFilters(prev => ({ ...prev, close_date: false }));
                          }
                        }}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded" 
                      />
                    </div>
                  )}
                </th>
                <th className="text-center w-[6%]"></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.length > 0 ? (
                filteredAndSorted.map((project) => (
                  <tr key={project.id} className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700" onClick={()=> window.location.href = `/projects/${project.project_uuid}`}>
                    {/* Müşteri adı - Proje adından önce */}
                    <td className="text-sm text-gray-900 dark:text-white">
                      {project.customer_name}
                    </td>
                    {/* Proje adı - Müşteri adından sonra */}
                    <td>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white flex items-center">
                          {project.project_visibility === 'Private' && (
                            <FiLock className="mr-2 text-red-500" size={14} />
                          )}
                          {project.project_name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {project.project_description}
                        </div>
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {project.assigned_to_name || '-'}
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      <div className="relative group inline-block">
                        {(() => {
                          const currency = (project.latest_offer_currency || 'TRY') === 'USD' ? 'USD' : 'TRY';
                          const total = Number(project.latest_offer_total || 0);
                          return total > 0
                            ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(total)
                            : '-';
                        })()}
                        {project.latest_offers_info && (
                          <div className="absolute z-10 hidden group-hover:block left-0 mt-1 w-64 p-3 rounded-md bg-gray-900 text-white text-xs shadow-lg">
                            <div className="font-medium mb-2">Geçmiş Teklifler</div>
                            <div className="space-y-1 max-h-64 overflow-auto">
                              {project.latest_offers_info.split(';').map((row, idx) => {
                                const [dateStr, totalStr, curr] = row.split('|');
                                const currCode = (curr || 'TRY') === 'USD' ? 'USD' : 'TRY';
                                const formatted = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currCode }).format(Number(totalStr || 0));
                                return (
                                  <div key={idx} className="flex items-center justify-between">
                                    <span className="opacity-80">{dateStr}</span>
                                    <span className="ml-3">{formatted}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {(() => {
                        const currency = (project.latest_offer_currency || 'TRY') === 'USD' ? 'USD' : 'TRY';
                        const profit = Number(project.latest_profitability || 0);
                        return profit > 0
                          ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(profit)
                          : '-';
                      })()}
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {(() => {
                        const total = Number(project.latest_offer_total || 0);
                        const profit = Number(project.latest_profitability || 0);
                        return total > 0 ? `${((profit / total) * 100).toFixed(2)}%` : '-';
                      })()}
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        project.project_status === 'Aktif' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        project.project_status === 'Beklemede' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        project.project_status === 'Başarılı Tamamlandı' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        project.project_status === 'Başarısız Tamamlandı' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        project.project_status === 'İptal' ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {project.project_status}
                      </span>
                    </td>
                    <td className="text-sm text-gray-900 dark:text-white">
                      {getOpportunityText(project.project_opportunity)}
                    </td>
                    <td className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(project.created_at).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="text-sm text-gray-500 dark:text-gray-400">
                      {project.close_date ? new Date(project.close_date).toLocaleDateString('tr-TR') : '-'}
                    </td>
                    <td className="text-center w-20" onClick={(e)=> e.stopPropagation()}>
                      <div className="relative action-menu">
                        {/* Guest kullanıcılar için action menüyü gizle */}
                        {/* User kullanıcılar sadece kendi projelerini düzenleyebilir */}
                        {currentUser?.user_type !== 'guest' && 
                         !(currentUser?.user_type === 'user' && project.assigned_to !== currentUser?.id) && (
                          <button onClick={(e)=> toggleActionMenu(project.id, e)} className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300 p-2 -m-2" title="İşlemler">
                            <CgMenuGridO size={18} />
                          </button>
                        )}
                        {showActionMenu === project.id && (
                          <div className="action-menu-dropdown fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl z-[9999] min-w-48">
                            <div className="py-1">
                              <button onClick={(e)=>{e.stopPropagation(); handleEditProject(project); setShowActionMenu(null);}} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <FiEdit className="mr-3" size={16} /> Düzenle
                              </button>
                              <button onClick={(e)=>{e.stopPropagation(); handleDeleteProject(project); setShowActionMenu(null);}} className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <FiTrash2 className="mr-3" size={16} /> Sil
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <FiFolder size={48} className="text-gray-300 dark:text-gray-600" />
                      <p className="text-gray-500 dark:text-gray-400">Henüz proje bulunmuyor</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sayfalama */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {/* Sol: Show records */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">records</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 font-medium whitespace-nowrap">
                {pagination.totalItems || 0} kayıt bulundu
              </div>
            </div>
            
            {/* Sağ: Para birimine göre toplam tutarlar - tag görünümlü, alt alta (sağa hizalı, biraz daha geniş) */}
            <div className="w-full flex justify-end">
              <div className="inline-block min-w-[420px] max-w-[560px] w-full sm:w-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                {/* Başlık satırı */}
                <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 rounded-t-lg">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Toplamlar</span>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {/* TRY satırı */}
                  <div className="flex items-center justify-between px-4 py-3">
                    {tableStats.TRY.count > 0 ? (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-200 dark:border-green-700">TL</span>
                        <div className="flex-1 flex items-center justify-end gap-6 text-sm">
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Proje Tutarı:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.TRY.amount, 'TRY')}</span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Kârlılık:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.TRY.profit, 'TRY')}</span>
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({tableStats.TRY.amount > 0 ? ((tableStats.TRY.profit / tableStats.TRY.amount) * 100).toFixed(1) : '0.0'}%)</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full text-sm font-medium text-red-600 dark:text-red-400">TL proje bulunamadı</div>
                    )}
                  </div>
                  {/* USD satırı */}
                  <div className="flex items-center justify-between px-4 py-3">
                    {tableStats.USD.count > 0 ? (
                      <>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border border-blue-200 dark:border-blue-700">USD</span>
                        <div className="flex-1 flex items-center justify-end gap-6 text-sm">
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Proje Tutarı:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.USD.amount, 'USD')}</span>
                          </div>
                          <div className="text-gray-600 dark:text-gray-300 whitespace-nowrap">
                            <span className="text-gray-500 dark:text-gray-400 mr-1">Kârlılık:</span>
                            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(tableStats.USD.profit, 'USD')}</span>
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({tableStats.USD.amount > 0 ? ((tableStats.USD.profit / tableStats.USD.amount) * 100).toFixed(1) : '0.0'}%)</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full text-sm font-medium text-red-600 dark:text-red-400">USD proje bulunamadı</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sayfa navigasyonu */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-end mt-4">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sayfa {pagination.currentPage} / {pagination.totalPages}
                </span>
                <div className="flex space-x-1">
                  {/* İlk sayfa */}
                  <button
                    onClick={() => handlePageChange(1)}
                    className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                      currentPage === 1
                        ? 'bg-brand-primary text-white shadow-lg'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                    }`}
                  >
                    1
                  </button>
                  
                  {/* Sol ellipsis */}
                  {currentPage > 4 && (
                    <span className="px-2 py-2 text-sm text-gray-500 font-medium">...</span>
                  )}
                  
                  {/* Orta sayfalar */}
                  {Array.from({ length: 3 }, (_, i) => {
                    let pageNum = currentPage - 1 + i;
                    if (pageNum < 2) pageNum = 2;
                    if (pageNum > pagination.totalPages - 1) pageNum = pagination.totalPages - 1;
                    if (pageNum === 1 || pageNum === pagination.totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                          currentPage === pageNum
                            ? 'bg-brand-primary text-white shadow-lg'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  }).filter(Boolean)}
                  
                  {/* Sağ ellipsis */}
                  {currentPage < pagination.totalPages - 3 && (
                    <span className="px-2 py-2 text-sm text-gray-500 font-medium">...</span>
                  )}
                  
                  {/* Son sayfa (1'den farklıysa) */}
                  {pagination.totalPages > 1 && (
                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      className={`w-10 h-10 text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-center ${
                        currentPage === pagination.totalPages
                          ? 'bg-brand-primary text-white shadow-lg'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-brand-primary hover:text-white border border-gray-300 dark:border-gray-600 shadow-md hover:shadow-lg'
                      }`}
                    >
                      {pagination.totalPages}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Uyarı mesajı - Hesaplanan rakamların gösterilen kayıt ve filtreler sonrasında oluştuğunu belirtir */}
        {tableStats.totalCount > 0 && (
          <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-600 dark:text-blue-300 text-center">
              <span className="font-medium">Not:</span> Yukarıdaki tutarlar sadece mevcut tabloda görünen {tableStats.totalCount} projeye aittir. 
              Filtreleme veya sayfa değişikliği yapıldığında bu tutarlar otomatik olarak güncellenir. Tüm kayıtlara ait değildir.
            </p>
          </div>
        )}
      </div>

      {/* Proje ekleme/düzenleme modal'ı */}
      {showModal && (
        <ProjectModal
          project={editingProject}
          onClose={() => {
            setShowModal(false);
            setEditingProject(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setEditingProject(null);
            queryClient.invalidateQueries('projects');
          }}
        />
      )}

      {/* Silme onay modal'ı */}
      {showDeleteModal && selectedProject && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          title="Projeyi Sil"
          message={`"${selectedProject.project_name}" projesini silmek istediğinizden emin misiniz?`}
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedProject(null);
          }}
          isLoading={deleteProjectMutation.isLoading}
        />
      )}
    </div>
  );
};

export default Projects;
